// components/events/addevents/RegistrationStep.jsx
// Step 3: Registration - Intent selection + registration/ticket configuration

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FaInfoCircle, FaRegCalendarAlt, FaRegClock } from 'react-icons/fa';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import {
  updateField,
  setExternalTicketUrl,
  addTicketType,
  updateTicketType,
  removeTicketType,
  addDiscountCode,
  updateDiscountCode,
  removeDiscountCode,
  addSeatingRow,
  updateRowLabel,
  updateSeatCount,
  removeSeatingRow,
  addRegistrationField,
  updateRegistrationField,
  updateFieldOptions,
  removeRegistrationField,
  moveRegistrationField,
  updateGstIncluded,
  setFormErrors,
} from '@/store/slices/events/eventSlice';
import SeatingLayoutSection from './SeatingLayoutSection';
import ResponsiveSheet from '@/components/shared/ResponsiveSheet';
import AttendeeInfoSection from '@/components/events/tickets/AttendeeInfoSection';
import { DEFAULT_BLOCK_MESSAGE } from '@/utils/events/registrationMessages';
import {
  addMenuFor,
  TYPE_LABEL,
  isChoiceType,
  isBlockableType,
  LABEL_MAX,
  OPTION_MAX,
  MIN_OPTIONS,
  SCALE_LABEL_MAX,
} from '@/utils/events/questionTypes';

// Grow a textarea to fit its content (no clipping, no scrollbar) - used by the question label and the
// option boxes so long questions/options wrap and show in full. Called on mount (ref) and on input.
const autoGrow = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

// A subtle "N/max" counter shown as soon as the field has any text (hidden on an empty field, so no
// "0/max" noise), so the host always sees how much room is left while typing.
const CharCounter = ({ value, max }) => {
  const len = (value || '').length;
  if (len === 0) return null;
  return <p className="text-xs text-slate-400 mt-1 text-right">{len}/{max}</p>;
};

// Day part (yyyy-mm-dd) of a date string/Date, in local terms — used to compare ticket/discount
// dates against the event day (by day, ignoring time).
const dayOf = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  try {
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  } catch { return ''; }
};

// Promotional Event Configuration
const PromotionalConfig = ({ formData, disabled, dispatch, errors }) => {
  const isPaidSelected = formData.isPaid === true;
  const isFreeSelected = formData.isPaid === false;

  return (
    <div className="space-y-4">
      {/* Free/Paid Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Event Type <span className="text-red-600">*</span>
        </label>
        <div className="inline-grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => !disabled && dispatch(updateField({ field: 'isPaid', value: false }))}
            disabled={disabled}
            className={`py-2 px-6 text-sm rounded-lg border transition-colors ${
              disabled
                ? 'bg-slate-100 text-slate-500 border-slate-200 cursor-not-allowed'
                : isFreeSelected
                  ? 'bg-teal-700 text-white border-teal-700'
                  : 'bg-white text-slate-700 border-slate-300'
            }`}
          >
            RSVP
          </button>
          <button
            type="button"
            onClick={() => !disabled && dispatch(updateField({ field: 'isPaid', value: true }))}
            disabled={disabled}
            className={`py-2 px-6 text-sm rounded-lg border transition-colors ${
              disabled
                ? 'bg-slate-100 text-slate-500 border-slate-200 cursor-not-allowed'
                : isPaidSelected
                  ? 'bg-teal-700 text-white border-teal-700'
                  : 'bg-white text-slate-700 border-slate-300'
            }`}
          >
            Paid
          </button>
        </div>
        {errors?.isPaid && (
          <p className="text-red-600 text-xs mt-1">
            {Array.isArray(errors.isPaid) ? errors.isPaid[0] : errors.isPaid}
          </p>
        )}
      </div>

      {/* External Ticket URL */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          External Registration Link
          <span className="text-slate-400 font-normal ml-1">(optional)</span>
        </label>
        <input
          type="url"
          value={formData.externalTicketUrl || ''}
          onChange={(e) => dispatch(setExternalTicketUrl(e.target.value))}
          disabled={disabled}
          className="w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder:text-slate-400 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 disabled:bg-slate-100 disabled:cursor-not-allowed"
          placeholder="https://eventbrite.com/your-event"
        />
        <p className="text-xs text-slate-500 mt-1">
          Link where attendees can register or purchase tickets
        </p>
        {errors?.externalTicketUrl && (
          <p className="text-red-600 text-xs mt-1">
            {Array.isArray(errors.externalTicketUrl) ? errors.externalTicketUrl[0] : errors.externalTicketUrl}
          </p>
        )}
      </div>

      {/* Promotional Summary */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
        <p className="text-sm text-slate-700">
          <span className="font-medium">Promotional Event:</span> Listed for visibility only. Registration and check-in features are not available.
        </p>
      </div>
    </div>
  );
};

// Capacity Management Section (shared between free and paid)
const CapacityManagement = ({ formData, errors, dispatch, isPaid }) => {
  const [bookingLimitError, setBookingLimitError] = useState('');

  const getInputClasses = (hasError) => {
    const base = 'w-full px-3 py-2.5 text-sm bg-white border rounded-lg text-slate-700 placeholder:text-slate-400 hover:border-slate-400 focus:outline-none focus:ring-2 focus:border-teal-400 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed';
    return `${base} ${hasError ? 'border-red-500 bg-red-50 focus:ring-red-300' : 'border-slate-300 focus:ring-teal-300'}`;
  };

  // Edit-locking D11: capacity can rise freely but can't drop below the committed count. Floor the
  // input and show an inline reason if the organiser types below it (the server re-checks at save).
  const committed = Number(formData.committedCount) || 0;
  const capacityBelowFloor =
    committed > 0 && formData.capacity !== '' && formData.capacity !== null && Number(formData.capacity) < committed;

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Event Capacity */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Capacity {isPaid
              ? <span className="text-red-600">*</span>
              : <span className="font-normal text-slate-400 ml-1">(optional)</span>}
          </label>
          <input
            type="number"
            min={committed > 0 ? String(committed) : '0'}
            inputMode="numeric"
            value={formData.capacity === '' ? '' : formData.capacity}
            onChange={(e) => {
              const val = e.target.value;
              dispatch(updateField({
                field: 'capacity',
                value: val === '' ? '' : Math.max(0, Number(val) || 0),
              }));
              if (errors?.capacity) {
                const newErrors = { ...errors };
                delete newErrors.capacity;
                dispatch(setFormErrors(newErrors));
              }
            }}
            onWheel={(e) => e.target.blur()}
            className={getInputClasses(!!errors?.capacity || capacityBelowFloor)}
            placeholder="0 = unlimited"
          />
          {errors?.capacity ? (
            <p className="text-red-600 text-xs mt-1">{errors.capacity}</p>
          ) : capacityBelowFloor ? (
            <p className="text-red-600 text-xs mt-1">Capacity can&apos;t be lower than the {committed} people already registered.</p>
          ) : (
            <p className="text-xs text-slate-500 mt-1">0 = unlimited capacity</p>
          )}
        </div>

        {/* Booking Limit */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Booking Limit <span className="text-red-600">*</span>
          </label>
          <input
            type="number"
            inputMode="numeric"
            value={formData.maxAttendeesPerRegistration === '' ? '' : formData.maxAttendeesPerRegistration}
            onChange={(e) => {
              const val = e.target.value;
              const numVal = Number(val);
              if (val !== '' && numVal === 0) {
                setBookingLimitError('Must be 1 or above');
              } else if (val !== '' && numVal < 0) {
                setBookingLimitError('Cannot be negative');
              } else {
                setBookingLimitError('');
              }
              dispatch(updateField({ field: 'maxAttendeesPerRegistration', value: val }));
              if (errors?.maxAttendeesPerRegistration) {
                const newErrors = { ...errors };
                delete newErrors.maxAttendeesPerRegistration;
                dispatch(setFormErrors(newErrors));
              }
            }}
            onWheel={(e) => e.target.blur()}
            className={getInputClasses(!!errors?.maxAttendeesPerRegistration || !!bookingLimitError)}
            placeholder="Max per order"
          />
          {errors?.maxAttendeesPerRegistration ? (
            <p className="text-red-600 text-xs mt-1">{errors.maxAttendeesPerRegistration}</p>
          ) : bookingLimitError ? (
            <p className="text-red-600 text-xs mt-1">{bookingLimitError}</p>
          ) : (
            <p className="text-xs text-slate-500 mt-1">Maximum seats per order</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom Registration Fields
const CustomRegistrationFields = ({ formData, errors, dispatch, isRecurring = false }) => {
  // Snapshot of which fields were flagged at the LAST validation attempt (Update / Next click).
  // Inline errors only render when a field is in this snapshot AND still currently invalid.
  // New issues that emerge from editing don't appear inline until the user clicks Update again.
  const [errorSnapshot, setErrorSnapshot] = useState({});
  // Which question card is currently expanded (null = all collapsed). Only one expanded at a time.
  const [expandedIndex, setExpandedIndex] = useState(null);
  // Which question is awaiting delete confirmation (null = none).
  const [confirmDeleteField, setConfirmDeleteField] = useState(null);
  // Attendee-view preview: open state + the throwaway answers typed inside the preview.
  const [showPreview, setShowPreview] = useState(false);
  const [previewResponses, setPreviewResponses] = useState({});

  useEffect(() => {
    const regErrors = errors?.registrationFields;
    if (!regErrors || regErrors.length === 0) {
      setErrorSnapshot({});
      return;
    }
    // Parse helper output ("Question N: <message>") into per-field keys.
    // Helper is sequential — at most one error per question, so the snapshot reflects exactly
    // what the toast showed (no extra "could-be" errors leaking in).
    const newSnapshot = {};
    regErrors.forEach((errMsg) => {
      const match = errMsg.match(/^Question (\d+): (.+)$/);
      if (!match) return;
      const idx = Number(match[1]) - 1;
      const msg = match[2];
      if (msg.startsWith('Type your question')) {
        newSnapshot[`${idx}-name`] = true;
      } else {
        // Everything else is an options-related error
        newSnapshot[`${idx}-options`] = true;
      }
    });
    setErrorSnapshot(newSnapshot);
    // Auto-expand the first question with an error so the host sees what to fix
    const errorKeys = Object.keys(newSnapshot);
    if (errorKeys.length > 0) {
      const firstIdx = parseInt(errorKeys[0].split('-')[0], 10);
      if (!isNaN(firstIdx)) setExpandedIndex(firstIdx);
    }
  }, [errors?.registrationFields]);

  // Remove a field and adjust expandedIndex so it still points at the same logical question
  const handleRemoveField = (index) => {
    dispatch(removeRegistrationField(index));
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
    setConfirmDeleteField(null);
  };

  // Move a question up (-1) or down (+1). Order is presentation only (answers are keyed by qid),
  // so it is always allowed, even on a locked/paid event. Keep any open editor pointed at the same
  // question as it moves.
  const handleMoveField = (index, direction) => {
    const list = formData.registrationFields || [];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    dispatch(moveRegistrationField({ index, direction }));
    if (expandedIndex === index) setExpandedIndex(target);
    else if (expandedIndex === target) setExpandedIndex(index);
    setConfirmDeleteField(null);
  };

  // Add a new field (defaults to text) and immediately expand it for editing; the
  // type is then picked inside the card (Google Forms pattern).
  const handleAddField = (opts) => {
    const newIndex = (formData.registrationFields || []).length;
    dispatch(addRegistrationField(opts));
    setExpandedIndex(newIndex);
  };

  // Change a question's type in place, re-shaping the card: a choice type needs at
  // least one option row to edit; a non-choice type carries no options; blocking
  // survives only on the blockable types (dropdown, multiple choice).
  const handleTypeChange = (index, newType) => {
    const f = (formData.registrationFields || [])[index];
    if (!f || f.fieldType === newType) return;
    dispatch(updateRegistrationField({ index, field: 'fieldType', value: newType }));
    if (isChoiceType(newType)) {
      // Coming FROM scale, fieldOptions held scale labels, not options - start fresh.
      if (f.fieldType === 'scale' || (f.fieldOptions || []).length === 0) {
        dispatch(updateFieldOptions({ index, options: [''] }));
      }
    } else {
      // Non-choice types carry no options; scale starts its labels empty.
      dispatch(updateFieldOptions({ index, options: [] }));
    }
    if (!isBlockableType(newType)) {
      dispatch(updateRegistrationField({ index, field: 'blockingOptions', value: [] }));
      dispatch(updateRegistrationField({ index, field: 'blockingMessage', value: '' }));
    }
    if (newType !== 'date' && f.includeTime) {
      dispatch(updateRegistrationField({ index, field: 'includeTime', value: false }));
    }
    clearSnapshotEntry(`${index}-options`);
  };

  // A question is "empty" when it has no text and no real options.
  const isEmptyField = (f) =>
    !f?.fieldName?.trim() && !(f?.fieldOptions || []).some((o) => o?.trim());

  // Done means "save this question", so it requires a valid one: a non-empty, unique label plus valid
  // options for the choice types. Invalid -> the sheet stays open with the error. A blank card is not
  // silently kept - dismiss it with Cancel / the X (which discards a blank), not Done.
  const handleDoneField = (index) => {
    const all = formData.registrationFields || [];
    const f = all[index];
    const snap = {};
    const name = (f.fieldName || '').trim();
    // Every question needs a label; and it must be unique within the event (case-insensitive).
    if (!name) snap[`${index}-name`] = true;
    else if (all.some((o, i) => i !== index && (o.fieldName || '').trim().toLowerCase() === name.toLowerCase())) {
      snap[`${index}-name`] = true;
    }
    if (isChoiceType(f.fieldType)) {
      const opts = f.fieldOptions || [];
      const lowered = opts.map((o) => (o || '').trim().toLowerCase());
      // A question may not block every answer (events-rsvp-blocking-questions.md): at least one must allow.
      const nonEmpty = opts.map((o) => (o || '').trim()).filter(Boolean);
      const blk = f.blockingOptions || [];
      const allBlocked = nonEmpty.length > 0 && nonEmpty.every((o) => blk.includes(o));
      const bad = opts.length < MIN_OPTIONS || opts.some((o) => !o?.trim()) || new Set(lowered).size !== lowered.length || allBlocked;
      if (bad) snap[`${index}-options`] = true;
    }
    if (Object.keys(snap).length) { setErrorSnapshot((prev) => ({ ...prev, ...snap })); return; } // stays open
    setErrorSnapshot((prev) => { const n = { ...prev }; delete n[`${index}-name`]; delete n[`${index}-options`]; return n; });
    setExpandedIndex(null);
  };

  // Cancel: discard a blank card, else just collapse (keeps typed values).
  const handleCancelField = (index) => {
    const f = (formData.registrationFields || [])[index];
    if (isEmptyField(f)) { handleRemoveField(index); return; }
    setErrorSnapshot((prev) => { const n = { ...prev }; delete n[`${index}-name`]; delete n[`${index}-options`]; return n; });
    setExpandedIndex(null);
  };

  const getNameError = (field, index) => {
    if (!errorSnapshot[`${index}-name`]) return null;
    return !field.fieldName?.trim() ? 'Type your question' : null;
  };
  const getOptionsError = (field, index) => {
    if (!errorSnapshot[`${index}-options`]) return null;
    if (!isChoiceType(field.fieldType)) return null;

    const options = field.fieldOptions || [];
    if (options.length < MIN_OPTIONS) return `Add at least ${MIN_OPTIONS} options`;
    if (options.some((o) => !o?.trim())) return 'Fill all options or remove the empty ones';
    const lowered = options.map((o) => o.trim().toLowerCase());
    if (new Set(lowered).size !== lowered.length) return 'Options must be unique';
    const nonEmpty = options.map((o) => (o || '').trim()).filter(Boolean);
    const blk = field.blockingOptions || [];
    if (nonEmpty.length > 0 && nonEmpty.every((o) => blk.includes(o))) return 'Leave at least one answer that allows registration';
    return null;
  };

  // Clear a field's snapshot entry as soon as the host takes any action on it.
  // The user has acknowledged the issue — don't surface new errors for that field until next Update.
  const clearSnapshotEntry = (key) => {
    setErrorSnapshot((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Disable "Add question / Add dropdown" buttons when any existing question is incomplete.
  // Stops hosts from stacking empty cards on top of unfinished ones.
  // An empty option ROW counts as incomplete — the question isn't ready until every row has text.
  const hasIncompleteQuestion = (formData.registrationFields || []).some((field) => {
    if (!field.fieldName?.trim()) return true;
    if (isChoiceType(field.fieldType)) {
      const options = field.fieldOptions || [];
      if (options.length === 0) return true;
      if (options.some((o) => !o?.trim())) return true;
      const lowered = options.map((o) => o.trim().toLowerCase());
      if (new Set(lowered).size !== lowered.length) return true;
    }
    return false;
  });

  // Duplicate question names (case-insensitive, D9). The later card in a clash is flagged; the server
  // re-checks on save. The indexes drive the inline "already used" message under the name field.
  const duplicateNameIndexes = (() => {
    const seen = new Map();
    const dups = new Set();
    (formData.registrationFields || []).forEach((f, i) => {
      const key = (f?.fieldName || '').trim().toLowerCase();
      if (!key) return;
      if (seen.has(key)) dups.add(i);
      else seen.set(key, i);
    });
    return dups;
  })();

  return (
    <div className="border-t border-slate-200 pt-6 mt-6">
      {/* Attendee Details: the fields the RSVP form ALWAYS collects, shown greyed so hosts
          see them and don't add duplicate name/mobile questions below. Free events only -
          this mirrors the real registration form. */}
      {!formData.isPaid && (
        <div className="mb-6">
          <h3 className="font-medium text-sm mb-1 text-slate-700">Attendee Details</h3>
          <p className="text-xs text-slate-500 mb-3">Collected automatically on every registration - no need to ask again below.</p>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Mobile Number</label>
              <input
                type="text"
                disabled
                placeholder="0412 345 678"
                className="w-full max-w-[220px] px-3 py-2 text-sm bg-slate-100 border border-slate-200 rounded-lg text-slate-400 placeholder:text-slate-400 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Attendee Name(s)</label>
              <input
                type="text"
                disabled
                placeholder="One name per attendee, up to the booking limit"
                className="w-full max-w-[320px] px-3 py-2 text-sm bg-slate-100 border border-slate-200 rounded-lg text-slate-400 placeholder:text-slate-400 cursor-not-allowed"
              />
            </div>
          </div>
        </div>
      )}

      <h3 className="font-medium text-sm mb-1 text-slate-700">
        Attendee Information
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Collect additional info
      </p>

      {(formData.registrationFields || []).length > 0 && (
        <div className="space-y-2 mb-4">
          {(formData.registrationFields || []).map((field, index) => {
            const isChoice = isChoiceType(field?.fieldType); // dropdown / multiple choice / checkboxes
            // Blocking answers (events-rsvp-blocking-questions.md) are free-RSVP + single-pick (dropdown
            // or multiple choice) only. Checkboxes (pick-many) never block.
            const showBlocking = isBlockableType(field?.fieldType) && !formData.isPaid;
            const blocks = field?.blockingOptions || [];
            const hasBlocks = blocks.length > 0;
            const nameError = getNameError(field, index);
            const optionsError = getOptionsError(field, index);
            const isDuplicateName = duplicateNameIndexes.has(index);
            const hasAnyError = !!(nameError || optionsError || isDuplicateName);
            // Post-activity edit rules (events-rsvp-question-editing.md). A question that existed at
            // load, once the event has answers: a PAID event freezes it fully; a FREE event opens it
            // for label-safe edits only (reword, add option, scale labels, block message) while the
            // type, existing options, blocking, includeTime, and delete stay locked.
            const activityLocked = !!formData.hasActivity && !!field._existing;
            const paidFrozen = activityLocked && !!formData.isPaid;
            const partialEdit = activityLocked && !formData.isPaid;
            // Existing (stored) options are locked; options appended this session (index at/after the
            // stored count) stay editable. Scale "options" are labels and are fully editable.
            const lockedOptionCount = partialEdit && isChoice ? (field._lockedOptions?.length || 0) : 0;
            // The editor opens in a bottom sheet (mobile) / dialog (desktop) - the shared
            // ResponsiveSheet, same as Preview - instead of expanding inline down the page.
            const isEditing = !paidFrozen && (expandedIndex === index || isEmptyField(field)
              || !!errorSnapshot[`${index}-name`] || !!errorSnapshot[`${index}-options`]);
            return (
              <div
                key={index}
                data-testid="rsvp-question-card"
                className={`border rounded-lg p-3 bg-white ${
                  hasAnyError ? 'border-red-300' : 'border-slate-200'
                }`}
              >
                {isEditing && (
                  <ResponsiveSheet
                    onClose={() => handleCancelField(index)}
                    ariaLabel="Edit question"
                    testId="question-editor-sheet"
                    header={
                      <h3 className="text-base font-semibold text-slate-900">
                        {field?._existing ? 'Edit question' : 'Add question'}
                      </h3>
                    }
                    footer={
                      <div className="flex justify-end gap-4">
                        <button type="button" onClick={() => handleCancelField(index)} className="text-sm font-medium text-slate-500 hover:text-slate-700">Cancel</button>
                        <button type="button" onClick={() => handleDoneField(index)} className="text-sm font-medium text-teal-700 hover:text-teal-800">Done</button>
                      </div>
                    }
                  >
                    <div className="px-4 py-4">
                    {/* A short note on an answered free question: what can and can't be changed. */}
                    {partialEdit && (
                      <p className="mb-3 text-xs text-amber-600">
                        This question has answers. You can reword it, add options, and edit scale labels - but its type, existing options, and blocking can't change, and it can't be deleted.
                      </p>
                    )}

                    {/* Question type — picked in-card; changing it re-shapes the editor below. Locked
                        once the question has answers (changing type would orphan them). */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-slate-700 mb-1">Question type</label>
                      <Listbox value={field?.fieldType || 'text'} onChange={(t) => handleTypeChange(index, t)} disabled={partialEdit}>
                        <div className="relative">
                          <ListboxButton className={`relative w-full sm:w-56 px-3 py-2.5 pr-9 text-sm text-left border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 ${partialEdit ? 'bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed' : 'bg-white border-slate-300 text-slate-700 cursor-pointer hover:border-slate-400'}`}>
                            <span className="block truncate">{TYPE_LABEL[field?.fieldType] || 'Text'}</span>
                            <svg
                              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </ListboxButton>
                          <ListboxOptions
                            anchor={{ to: 'bottom start', gap: 4, padding: 16 }}
                            className="z-50 w-[var(--button-width)] max-h-60 overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-slate-200 focus:outline-none"
                          >
                            {addMenuFor(formData.isPaid).map((item) => (
                              <ListboxOption
                                key={item.type}
                                value={item.type}
                                className={({ active }) =>
                                  `cursor-pointer select-none py-2 px-3 ${active ? 'bg-teal-50' : ''}`
                                }
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={`block text-slate-900 ${selected ? 'font-semibold' : ''}`}>{item.label}</span>
                                    {item.hint && <span className="block text-xs text-slate-500">{item.hint}</span>}
                                  </>
                                )}
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </div>
                      </Listbox>
                    </div>

                    {/* Question text - an auto-growing textarea so a long question wraps and shows in
                        full (Enter adds a line break). */}
                    <textarea
                      ref={autoGrow}
                      rows={1}
                      value={field?.fieldName || ''}
                      onChange={(e) => {
                        dispatch(updateRegistrationField({ index, field: 'fieldName', value: e.target.value }));
                        clearSnapshotEntry(`${index}-name`);
                        autoGrow(e.target);
                      }}
                      className={`w-full resize-none overflow-hidden px-3 py-2.5 text-sm bg-white border rounded-lg text-slate-700 placeholder:text-slate-400 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 ${
                        nameError || isDuplicateName ? 'border-red-500 bg-red-50' : 'border-slate-300'
                      }`}
                      placeholder={isChoice ? 'Enter your question (e.g. T-shirt size)' : 'Enter your question (e.g. Dietary requirements)'}
                      maxLength={LABEL_MAX}
                    />
                    <CharCounter value={field?.fieldName} max={LABEL_MAX} />
                    {nameError && (
                      <p className="text-xs text-red-600 mt-1">{nameError}</p>
                    )}
                    {!nameError && isDuplicateName && (
                      <p className="text-xs text-red-600 mt-1">Another question already uses this name.</p>
                    )}

                    {/* Required. Sits right under the question row, far right. A blocking question
                        is forced Required and the toggle is locked on (D). */}
                    <div className="flex items-center justify-end mt-2">
                      {hasBlocks && (
                        <span className="mr-2 text-xs text-slate-400">(locked on - an answer blocks registration, so this must be answered)</span>
                      )}
                      <input
                        type="checkbox"
                        id={`required-${index}`}
                        checked={field?.isRequired || hasBlocks}
                        disabled={hasBlocks}
                        onChange={(e) => dispatch(updateRegistrationField({ index, field: 'isRequired', value: e.target.checked }))}
                        className="w-4 h-4 accent-teal-700 border-slate-300 rounded focus:ring-teal-300 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
                      />
                      <label htmlFor={`required-${index}`} className="ml-2 text-sm text-slate-700">
                        Required to register
                      </label>
                    </div>

                    {/* Scale editor: the fixed 1-5 numbers, each with an optional label box.
                        Labels are stored positionally in fieldOptions (slot 0 = number 1) and
                        appear under the scale on the attendee form. */}
                    {field.fieldType === 'scale' && (
                      <div className="mt-3 pl-3 border-l-2 border-teal-200">
                        <div className="space-y-2">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <div key={n}>
                            <div className="flex items-center gap-2">
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-300 text-sm text-slate-500">
                                {n}
                              </span>
                              <input
                                type="text"
                                value={field.fieldOptions?.[n - 1] || ''}
                                onChange={(e) => {
                                  const labels = [...(field.fieldOptions || [])];
                                  while (labels.length < 5) labels.push('');
                                  labels[n - 1] = e.target.value;
                                  dispatch(updateFieldOptions({ index, options: labels }));
                                }}
                                maxLength={SCALE_LABEL_MAX}
                                className="flex-1 px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder:text-slate-400 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400"
                                placeholder={n === 1 ? 'What 1 means (optional), e.g. nothing' : n === 5 ? 'What 5 means (optional), e.g. fluent' : 'optional'}
                              />
                            </div>
                            <CharCounter value={field.fieldOptions?.[n - 1]} max={SCALE_LABEL_MAX} />
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-slate-500 mt-2">Attendees pick one number. Filled labels appear under the scale.</p>
                      </div>
                    )}

                    {/* Date / time / yes-no: nothing to configure, so show a mock of what attendees get.
                        A clear calendar/clock icon + the format reads as a date picker (a plain greyed
                        native box looked like a broken dropdown). */}
                    {(field.fieldType === 'date' || field.fieldType === 'time') && (
                      <div className="mt-3 pl-3 border-l-2 border-teal-200">
                        <div className={`${field.includeTime ? 'w-64' : field.fieldType === 'time' ? 'w-36' : 'w-48'} flex items-center gap-2 px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-lg text-slate-400`}>
                          {field.fieldType === 'time' ? (
                            <FaRegClock className="h-4 w-4 shrink-0 text-slate-400" />
                          ) : (
                            <FaRegCalendarAlt className="h-4 w-4 shrink-0 text-slate-400" />
                          )}
                          <span>{field.fieldType === 'time' ? '- - : - -' : field.includeTime ? 'DD/MM/YYYY, - - : - -' : 'DD/MM/YYYY'}</span>
                        </div>
                        {field.fieldType === 'date' && (
                          <label className={`mt-2 flex items-center gap-2 text-sm ${partialEdit ? 'text-slate-400' : 'text-slate-700'}`}>
                            <input
                              type="checkbox"
                              checked={!!field.includeTime}
                              disabled={partialEdit}
                              onChange={(e) => dispatch(updateRegistrationField({ index, field: 'includeTime', value: e.target.checked }))}
                              className="w-4 h-4 accent-teal-700 border-slate-300 rounded focus:ring-teal-300 disabled:cursor-not-allowed"
                            />
                            Also ask time
                          </label>
                        )}
                        <p className="text-xs text-slate-500 mt-2">
                          {field.fieldType === 'time'
                            ? 'Attendees pick a time in this box.'
                            : field.includeTime
                              ? 'Attendees pick a date and a time in this one box.'
                              : 'Attendees pick a date from this calendar box.'}
                        </p>
                      </div>
                    )}
                    {field.fieldType === 'yesno' && (
                      <div className="mt-3 pl-3 border-l-2 border-teal-200">
                        <div className="flex items-center gap-2">
                          <input type="checkbox" disabled className="w-4 h-4 rounded border-slate-300 cursor-not-allowed" />
                          <span className="text-sm text-slate-500 italic">Your question appears here with one tick box</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Ticked = Yes, unticked = No. If required, attendees must tick it to register.
                        </p>
                      </div>
                    )}

                    {/* Options editor (choice types: dropdown, multiple choice, checkboxes) */}
                    {isChoice && (
                      <div className="mt-3 pl-3 border-l-2 border-teal-200">
                        <label className="block text-xs font-medium text-slate-700 mb-2">Options</label>
                        <div className="space-y-2">
                          {(field?.fieldOptions || []).map((option, optIdx) => {
                            const optionLocked = partialEdit && optIdx < lockedOptionCount; // existing option
                            return (
                            <div key={optIdx}>
                            <div className="flex gap-2 items-start">
                              {/* Type marker (Google Forms pattern): a dropdown's options read as a
                                  numbered list, multiple choice as circles, checkboxes as squares -
                                  so the host sees how the question will render while building it. mt
                                  keeps the marker aligned to the option's first line as it wraps. */}
                              {field.fieldType === 'select' ? (
                                <span className="shrink-0 mt-2.5 w-5 text-right text-sm text-slate-400">{optIdx + 1}.</span>
                              ) : field.fieldType === 'radio' ? (
                                <span className="shrink-0 mt-3 w-4 h-4 rounded-full border-2 border-slate-300" aria-hidden="true"></span>
                              ) : (
                                <span className="shrink-0 mt-3 w-4 h-4 rounded border-2 border-slate-300" aria-hidden="true"></span>
                              )}
                              <textarea
                                ref={autoGrow}
                                rows={1}
                                value={option || ''}
                                disabled={optionLocked}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                                onChange={(e) => {
                                  const oldVal = field.fieldOptions?.[optIdx] || '';
                                  const newVal = e.target.value;
                                  autoGrow(e.target);
                                  const newOptions = [...(field.fieldOptions || [])];
                                  newOptions[optIdx] = newVal;
                                  dispatch(updateFieldOptions({ index, options: newOptions }));
                                  // Blocking values are stored BY VALUE - follow a rename so the flag isn't lost.
                                  if ((field.blockingOptions || []).includes(oldVal)) {
                                    dispatch(updateRegistrationField({ index, field: 'blockingOptions', value: (field.blockingOptions || []).map((v) => (v === oldVal ? newVal : v)) }));
                                  }
                                  clearSnapshotEntry(`${index}-options`);
                                }}
                                className={`min-w-0 flex-1 resize-none overflow-hidden px-3 py-2.5 text-sm border border-slate-300 rounded-lg placeholder:text-slate-400 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 disabled:bg-slate-100 disabled:cursor-not-allowed ${
                                  optionLocked ? 'bg-slate-100 text-slate-500' : 'bg-white text-slate-700'
                                }`}
                                placeholder={`Option ${optIdx + 1}`}
                                maxLength={OPTION_MAX}
                              />
                              {!optionLocked && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const removedVal = field.fieldOptions?.[optIdx] || '';
                                    const newOptions = (field.fieldOptions || []).filter((_, i) => i !== optIdx);
                                    dispatch(updateFieldOptions({ index, options: newOptions }));
                                    if ((field.blockingOptions || []).includes(removedVal)) {
                                      dispatch(updateRegistrationField({ index, field: 'blockingOptions', value: (field.blockingOptions || []).filter((v) => v !== removedVal) }));
                                    }
                                    clearSnapshotEntry(`${index}-options`);
                                  }}
                                  className="mt-1 p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  aria-label="Remove option"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {/* Char counter + block-if-chosen share one right-aligned line under the
                                option, instead of stacking two - the counter alone would waste a row.
                                Blocking: picking this answer blocks the RSVP (free dropdowns only);
                                frozen once the question has answers. */}
                            {(() => {
                              const len = (option || '').length;
                              const showBlockToggle = showBlocking && !partialEdit && (option || '').trim();
                              if (!len && !showBlockToggle) return null;
                              return (
                                <div className="flex items-center justify-end gap-3 mt-1">
                                  {len > 0 && <span className="text-xs text-slate-400">{len}/{OPTION_MAX}</span>}
                                  {showBlockToggle && (
                                    <label className="flex items-center gap-1.5 text-xs text-slate-600" title="An attendee choosing this answer cannot register">
                                      <input
                                        type="checkbox"
                                        checked={(field.blockingOptions || []).includes(option)}
                                        onChange={(e) => {
                                          const cur = field.blockingOptions || [];
                                          const next = e.target.checked ? [...new Set([...cur, option])] : cur.filter((v) => v !== option);
                                          dispatch(updateRegistrationField({ index, field: 'blockingOptions', value: next }));
                                          if (next.length > 0) dispatch(updateRegistrationField({ index, field: 'isRequired', value: true }));
                                          clearSnapshotEntry(`${index}-options`);
                                        }}
                                        className="w-3.5 h-3.5 accent-teal-700 rounded focus:ring-teal-300"
                                      />
                                      Block registration
                                    </label>
                                  )}
                                </div>
                              );
                            })()}
                            </div>
                          ); })}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newOptions = [...(field.fieldOptions || []), ''];
                            dispatch(updateFieldOptions({ index, options: newOptions }));
                            clearSnapshotEntry(`${index}-options`);
                          }}
                          disabled={(field.fieldOptions || []).some((o) => !o?.trim())}
                          className="mt-2 inline-flex items-center px-3 py-1.5 text-xs text-teal-700 hover:text-teal-800 font-medium disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed disabled:hover:text-teal-700"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add option
                        </button>
                        {optionsError && (
                          <p className="text-xs text-red-600 mt-2">{optionsError}</p>
                        )}
                      </div>
                    )}

                    {/* Message shown when blocked (free dropdown with a block set): optional, 150 cap. */}
                    {showBlocking && hasBlocks && (
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-slate-700 mb-1">
                          Message shown when registration is blocked <span className="font-normal text-slate-400">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={field.blockingMessage || ''}
                          onChange={(e) => dispatch(updateRegistrationField({ index, field: 'blockingMessage', value: e.target.value }))}
                          placeholder="e.g. Sorry, this event is 18+ only."
                          maxLength="150"
                          className="w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder:text-slate-400 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400"
                        />
                        <p className="text-xs text-slate-400 mt-1 text-right">{(field.blockingMessage || '').length}/150</p>
                      </div>
                    )}

                    {formData.hasActivity && !field._existing && field.isRequired && (
                      <p className="text-xs text-amber-600 mt-2">
                        This event already has attendees. A new required question only applies to people who register from now on - those who already registered will not have answered it.
                      </p>
                    )}

                    </div>
                  </ResponsiveSheet>
                )}
                {/* Collapsed summary always visible; the editor opens in the sheet above. */}
                <div className="flex items-center justify-between gap-2">
                    {/* Two lines so a long label keeps the full width on a phone instead of fighting
                        the four action icons for space: label on top, type/required meta below. */}
                    <div className="min-w-0 flex-1 text-sm">
                      <span data-testid="rsvp-question-card-label" className="block truncate text-slate-700">
                        {field?.fieldName?.trim() ? field.fieldName : <span className="italic text-slate-400">Untitled question</span>}
                      </span>
                      <span className="block truncate text-xs font-normal text-slate-400">
                        {TYPE_LABEL[field?.fieldType] || 'Text'}{field?.fieldType === 'date' && field?.includeTime ? ' + time' : ''}{field?.isRequired ? ' · Required' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Move up/down: reordering is presentation only (answers keyed by qid), so it
                          is offered on every question, even a locked/paid one. Disabled at the ends. */}
                      <button
                        type="button"
                        onClick={() => handleMoveField(index, -1)}
                        disabled={index === 0}
                        aria-label="Move question up"
                        className="p-2 text-slate-500 hover:text-teal-700 hover:bg-slate-50 rounded-lg transition-colors disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveField(index, 1)}
                        disabled={index === (formData.registrationFields || []).length - 1}
                        aria-label="Move question down"
                        className="p-2 text-slate-500 hover:text-teal-700 hover:bg-slate-50 rounded-lg transition-colors disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {paidFrozen ? (
                        <span className="text-xs text-slate-400 italic">Attendees registered - locked</span>
                      ) : partialEdit ? (
                        // Free event with answers: editable (label-safe) but not deletable - Edit only.
                        <button
                          type="button"
                          onClick={() => setExpandedIndex(index)}
                          aria-label="Edit question"
                          className="p-2 text-slate-500 hover:text-teal-700 hover:bg-slate-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      ) : confirmDeleteField === index ? (
                        <span className="flex items-center gap-2 text-xs text-slate-600">
                          Delete?
                          <button
                            type="button"
                            onClick={() => { handleRemoveField(index); setConfirmDeleteField(null); }}
                            className="font-medium text-red-600 hover:text-red-700"
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteField(null)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            No
                          </button>
                        </span>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setExpandedIndex(index)}
                            aria-label="Edit question"
                            className="p-2 text-slate-500 hover:text-teal-700 hover:bg-slate-50 rounded-lg transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteField(index)}
                            aria-label="Delete question"
                            className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Top-level error box removed — errors now show inline under each input.
          Toast on Next/Submit still summarises all errors. */}

      {/* One Add question button (Google Forms pattern) — the type is picked inside the card.
          Disabled when any existing question is incomplete, so hosts can't pile up empty cards
          on top of unfinished ones. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <button
          type="button"
          onClick={() => handleAddField()}
          disabled={hasIncompleteQuestion}
          className="inline-flex items-center text-sm font-medium text-teal-700 hover:text-teal-800 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed disabled:hover:text-teal-700"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add question
        </button>
        {/* Preview: opens the REAL attendee component (AttendeeInfoSection) in the shared sheet,
            live - so what the host sees here is exactly what registrants get, blocking included. */}
        {(formData.registrationFields || []).length > 0 && (
          <button
            type="button"
            onClick={() => { setPreviewResponses({}); setShowPreview(true); }}
            className="ml-auto inline-flex items-center text-sm font-medium text-teal-700 hover:text-teal-800"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Preview
          </button>
        )}
        {hasIncompleteQuestion && (
          <p className="w-full text-xs text-slate-500 mt-1">
            Complete the question above to add another.
          </p>
        )}
      </div>

      {showPreview && (
        <ResponsiveSheet
          onClose={() => setShowPreview(false)}
          ariaLabel="Attendee view preview"
          header={
            <div>
              <h3 className="text-base font-semibold text-slate-900">Attendee view</h3>
              <p className="text-xs text-slate-500">How your questions appear on the registration form. Try them - nothing is saved.</p>
            </div>
          }
        >
          <div className="px-4 pb-6">
            <AttendeeInfoSection
              event={{ registrationFields: formData.registrationFields }}
              formResponses={previewResponses}
              onFormResponseUpdate={(fieldName, value) =>
                setPreviewResponses((prev) => ({ ...prev, [fieldName]: value }))
              }
              blockMessageFor={
                formData.isPaid
                  ? undefined
                  : (field) => {
                      const v = previewResponses[field.fieldName];
                      if (!v || !(field.blockingOptions || []).includes(v)) return null;
                      return (field.blockingMessage || '').trim() || DEFAULT_BLOCK_MESSAGE;
                    }
              }
              singleColumn
              bare
            />
          </div>
        </ResponsiveSheet>
      )}
    </div>
  );
};

// Ticket Types Section (for paid events)
const TicketTypesSection = ({ formData, errors, dispatch }) => {
  const [priceErrors, setPriceErrors] = useState({});
  // Ticket accordion: which row is expanded (null = none) + per-row errors captured on Done.
  const [editingTicket, setEditingTicket] = useState(null);
  const [ticketRowErrors, setTicketRowErrors] = useState({});
  // Which row is awaiting delete confirmation (null = none).
  const [confirmDeleteTicket, setConfirmDeleteTicket] = useState(null);

  const validatePriceDecimals = (value) => {
    if (!value || value === '') return true;
    const strValue = value.toString();
    const decimalIndex = strValue.indexOf('.');
    if (decimalIndex === -1) return true;
    return strValue.length - decimalIndex - 1 <= 2;
  };

  const getInputClasses = (hasError) => {
    const base = 'w-full px-3 py-2.5 text-sm text-slate-700 bg-white [color-scheme:light] placeholder:text-slate-400 hover:border-slate-400 border rounded-lg focus:outline-none focus:ring-2 focus:border-teal-400 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed';
    return `${base} ${hasError ? 'border-red-500 bg-red-50 focus:ring-red-300' : 'border-slate-300 focus:ring-teal-300'}`;
  };

  // --- Ticket accordion helpers ---
  const priceIsEmpty = (p) => p === null || p === undefined || p === '';
  const isEmptyTicket = (t) =>
    !t?.name?.trim() && priceIsEmpty(t?.price) && !t?.startDate && !t?.expiryDate;

  const eventDay = dayOf(formData.dateTime);
  const maxPer = Number(formData.maxAttendeesPerRegistration) || 0;

  // A ticket type is a bundle when its bundleSize holds a value (empty = normal single ticket).
  const isBundle = (t) => t?.bundleSize !== '' && t?.bundleSize !== null && t?.bundleSize !== undefined;

  // Same rule as the step validator (name + price required, $1 min, 2 decimals, unique) + date window.
  const validateTicket = (t, index) => {
    const errs = {};
    if (!t?.name?.trim()) errs.name = 'Name is required';
    else {
      // Ticket names must be unique within the event (AC10).
      const dup = (formData.ticketTypes || []).some(
        (o, i) => i !== index && o?.name?.trim() && o.name.trim().toLowerCase() === t.name.trim().toLowerCase()
      );
      if (dup) errs.name = 'A ticket type with this name already exists';
    }
    if (priceIsEmpty(t?.price)) errs.price = 'Price is required';
    else if (!validatePriceDecimals(t.price)) errs.price = 'Maximum 2 decimal places allowed';
    else if (Number(t.price) < 1) errs.price = 'Minimum ticket price is $1';
    // Bundle rules mirror the server: size >= 2 (whole), no bigger than the per-order limit, and the
    // pack price (the Price field) must leave each person's share at least $1 (AC12 wording).
    if (isBundle(t)) {
      const size = Number(t.bundleSize);
      const price = Number(t.price);
      if (!Number.isInteger(size) || size < 2) {
        errs.bundle = 'Bundle size must be a whole number of 2 or more';
      } else if (maxPer && size > maxPer) {
        errs.bundle = `Bundle size can't be more than the ${maxPer} tickets allowed per order`;
      } else if (!priceIsEmpty(t?.price) && !isNaN(price) && price / size < 1) {
        errs.bundle = `Bundle price must be at least $${size} - each of the ${size} tickets must be at least $1.`;
      }
    }
    // Date window: start/expiry not in the past (AC11), on or before the event day, expiry >= start.
    const today = dayOf(new Date());
    const start = dayOf(t?.startDate);
    const expiry = dayOf(t?.expiryDate);
    if (start && start < today) errs.dates = "Start date can't be in the past";
    else if (expiry && expiry < today) errs.dates = "Expiry date can't be in the past";
    else if (eventDay && start && start > eventDay) errs.dates = "Start date can't be after the event day";
    else if (eventDay && expiry && expiry > eventDay) errs.dates = "Expiry date can't be after the event day";
    else if (start && expiry && expiry < start) errs.dates = 'Expiry must be on or after the start';
    return errs;
  };

  // Show the per-row (Done) error first, else the global Next/Submit error, else the live price check.
  const ticketNameError = (index) => ticketRowErrors[index]?.name || errors?.[`ticketName_${index}`]?.[0];
  const ticketPriceError = (index) =>
    ticketRowErrors[index]?.price || errors?.[`ticketPrice_${index}`]?.[0] || priceErrors[index];
  const ticketDateError = (index) => ticketRowErrors[index]?.dates || errors?.[`ticketDate_${index}`]?.[0];
  const ticketBundleError = (index) => ticketRowErrors[index]?.bundle || errors?.[`ticketBundle_${index}`]?.[0];

  // Never collapse a blank or invalid row, so its error is always reachable before Next.
  const isTicketOpen = (t, index) =>
    editingTicket === index ||
    isEmptyTicket(t) ||
    !!ticketRowErrors[index] ||
    !!errors?.[`ticketName_${index}`] ||
    !!errors?.[`ticketPrice_${index}`] ||
    !!errors?.[`ticketDate_${index}`] ||
    !!errors?.[`ticketBundle_${index}`] ||
    !!priceErrors[index];

  const clearTicketRowError = (index, field) => {
    setTicketRowErrors((prev) => {
      if (!prev[index]?.[field]) return prev;
      const nextRow = { ...prev[index] };
      delete nextRow[field];
      const next = { ...prev };
      if (Object.keys(nextRow).length) next[index] = nextRow;
      else delete next[index];
      return next;
    });
  };

  const handleAddTicket = () => {
    const newIndex = (formData.ticketTypes || []).length;
    dispatch(addTicketType());
    setEditingTicket(newIndex); // open the new row
    if (errors?.ticketTypes) {
      const next = { ...errors };
      delete next.ticketTypes;
      dispatch(setFormErrors(next));
    }
  };

  const handleRemoveTicket = (index) => {
    dispatch(removeTicketType(index));
    // Indices shift on remove; reset local row state to avoid mismatches.
    setEditingTicket(null);
    setTicketRowErrors({});
    setPriceErrors({});
  };

  const handleDoneTicket = (index) => {
    const t = (formData.ticketTypes || [])[index];
    if (isEmptyTicket(t)) {
      if ((formData.ticketTypes || []).length > 1) dispatch(removeTicketType(index));
      setEditingTicket(null);
      return;
    }
    const errs = validateTicket(t, index);
    if (Object.keys(errs).length > 0) {
      setTicketRowErrors((prev) => ({ ...prev, [index]: errs }));
      setEditingTicket(index); // keep open to show errors
      return;
    }
    setTicketRowErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setEditingTicket(null); // valid -> collapse
  };

  // Cancel = simple close: discard a blank new row, else just collapse (keeps typed values;
  // the Next/Submit safety net still catches an invalid row).
  const handleCancelTicket = (index) => {
    const t = (formData.ticketTypes || [])[index];
    if (isEmptyTicket(t) && (formData.ticketTypes || []).length > 1) {
      dispatch(removeTicketType(index));
    }
    setTicketRowErrors((prev) => { const next = { ...prev }; delete next[index]; return next; });
    setPriceErrors((prev) => { const next = { ...prev }; delete next[index]; return next; });
    setEditingTicket(null);
  };

  return (
    <div className="border-t border-slate-200 pt-6 mt-6">
      <h3 className="font-medium text-sm mb-4 text-slate-700">
        Tickets Configuration <span className="text-red-600">*</span>
      </h3>

      {(formData.ticketTypes || []).length > 0 && (
        <div className="space-y-3 mb-4">
          {(formData.ticketTypes || []).map((ticket, index) => {
            // Collapsed summary row: Name · $Price + edit/delete icons.
            if (!isTicketOpen(ticket, index)) {
              return (
                <div
                  key={index}
                  className="flex items-center justify-between gap-2 p-3 border border-slate-200 rounded-lg"
                >
                  <p className="min-w-0 truncate text-sm font-medium text-slate-700">
                    {ticket.name}
                    <span className="font-normal text-slate-500"> · ${Number(ticket.price).toFixed(2)}{isBundle(ticket) ? ` · admits ${ticket.bundleSize}` : ''}</span>
                    {ticket.active === false && <span className="font-normal text-slate-400"> · Closed</span>}
                  </p>
                  <div className="flex items-center gap-1 shrink-0">
                    {confirmDeleteTicket === index ? (
                      <span className="flex items-center gap-2 text-xs text-slate-600">
                        Delete?
                        <button
                          type="button"
                          onClick={() => { handleRemoveTicket(index); setConfirmDeleteTicket(null); }}
                          className="font-medium text-red-600 hover:text-red-700"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteTicket(null)}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          No
                        </button>
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setEditingTicket(index)}
                          aria-label="Edit ticket type"
                          className="p-2 text-slate-500 hover:text-teal-700 hover:bg-slate-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {(formData.ticketTypes || []).length > 1 && !ticket.hasSold && (
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteTicket(index)}
                            aria-label="Delete ticket type"
                            className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            }

            // Expanded editor. A sold type (edit-locking D8) freezes its name/price/bundle - note and
            // sale dates stay editable (D13). soldLock also appends a disabled look to those inputs.
            const soldLock = !!ticket.hasSold;
            const soldClass = soldLock ? ' bg-slate-100 text-slate-500 cursor-not-allowed' : '';
            return (
              <div key={index} className="border border-slate-200 rounded-lg p-3">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-700">
                    Ticket {index + 1}{ticket?.name ? `: ${ticket.name}` : ''}
                  </span>
                  {/* On-sale switch: stop selling this type without deleting it. Stays available even
                      after a sale - it's how an organiser retires a sold type (delete is blocked). */}
                  <span className="flex items-center gap-2">
                    <span className={`text-xs ${ticket.active !== false ? 'text-teal-700 font-medium' : 'text-slate-400'}`}>
                      {ticket.active !== false ? 'On sale' : 'Stopped'}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={ticket.active !== false}
                      aria-label="On sale"
                      onClick={() => {
                        setEditingTicket(index);
                        dispatch(updateTicketType({ index, field: 'active', value: !(ticket.active !== false) }));
                        if (errors?.ticketTypes) { const n = { ...errors }; delete n.ticketTypes; dispatch(setFormErrors(n)); }
                      }}
                      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${ticket.active !== false ? 'bg-teal-700' : 'bg-slate-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${ticket.active !== false ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                    </button>
                  </span>
                </div>
                {ticket.active === false && (
                  <p className="text-xs text-amber-600 mb-3">
                    Stopped - buyers can&apos;t purchase this type. Anyone who already bought keeps their ticket.
                  </p>
                )}
                {soldLock && (
                  <p className="text-xs text-slate-500 mb-3">
                    Tickets have sold, so this type&apos;s name, price and bundle are locked. You can still edit its note and sale dates.
                  </p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Ticket Name */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={ticket?.name || ''}
                      onChange={(e) => {
                        setEditingTicket(index); // claim this row so it stays open while typing
                        dispatch(updateTicketType({ index, field: 'name', value: e.target.value }));
                        if (errors?.[`ticketName_${index}`]) {
                          const newErrors = { ...errors };
                          delete newErrors[`ticketName_${index}`];
                          dispatch(setFormErrors(newErrors));
                        }
                        clearTicketRowError(index, 'name');
                      }}
                      placeholder="Early Bird"
                      disabled={soldLock}
                      className={getInputClasses(!!ticketNameError(index)) + soldClass}
                      maxLength="100"
                    />
                    {ticketNameError(index) && (
                      <p className="text-red-600 text-xs mt-1">{ticketNameError(index)}</p>
                    )}
                  </div>

                  {/* Ticket Price (whole-bundle price when this type is a bundle) */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      {isBundle(ticket) ? 'Bundle price' : 'Price'} <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      value={ticket.price === undefined || ticket.price === null ? '' : ticket.price}
                      onChange={(e) => {
                        setEditingTicket(index); // claim this row so it stays open while typing
                        const val = e.target.value;
                        if (!validatePriceDecimals(val)) {
                          setPriceErrors({ ...priceErrors, [index]: 'Maximum 2 decimal places allowed' });
                        } else if (val !== '' && Number(val) < 1) {
                          setPriceErrors({ ...priceErrors, [index]: 'Minimum ticket price is $1' });
                        } else {
                          const newErrors = { ...priceErrors };
                          delete newErrors[index];
                          setPriceErrors(newErrors);
                        }
                        dispatch(updateTicketType({ index, field: 'price', value: val }));
                        if (errors?.[`ticketPrice_${index}`]) {
                          const newErrors = { ...errors };
                          delete newErrors[`ticketPrice_${index}`];
                          dispatch(setFormErrors(newErrors));
                        }
                        clearTicketRowError(index, 'price');
                      }}
                      onWheel={(e) => e.target.blur()}
                      disabled={soldLock}
                      className={getInputClasses(!!ticketPriceError(index)) + soldClass}
                      placeholder="0.00"
                    />
                    {ticketPriceError(index) && (
                      <p className="text-red-600 text-xs mt-1">{ticketPriceError(index)}</p>
                    )}
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Start Date <span className="font-normal text-slate-400">(optional)</span></label>
                    <input
                      type="date"
                      value={ticket.startDate ? (ticket.startDate.includes('T') ? ticket.startDate.slice(0, 10) : ticket.startDate) : ''}
                      onChange={(e) => { setEditingTicket(index); dispatch(updateTicketType({ index, field: 'startDate', value: e.target.value || null })); }}
                      onWheel={(e) => e.target.blur()}
                      max={eventDay || undefined}
                      className={getInputClasses(!!ticketDateError(index))}
                    />
                  </div>

                  {/* Expiry Date */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Expiry Date <span className="font-normal text-slate-400">(optional)</span></label>
                    <input
                      type="date"
                      value={ticket.expiryDate ? (ticket.expiryDate.includes('T') ? ticket.expiryDate.slice(0, 10) : ticket.expiryDate) : ''}
                      onChange={(e) => { setEditingTicket(index); dispatch(updateTicketType({ index, field: 'expiryDate', value: e.target.value || null })); }}
                      onWheel={(e) => e.target.blur()}
                      min={dayOf(ticket.startDate) || undefined}
                      max={eventDay || undefined}
                      className={getInputClasses(!!ticketDateError(index))}
                    />
                  </div>
                </div>
                {ticketDateError(index) && (
                  <p className="text-red-600 text-xs mt-1">{ticketDateError(index)}</p>
                )}

                {/* Optional display-only note shown to buyers while choosing (e.g. "Includes dinner"). */}
                <div className="mt-3">
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Note <span className="font-normal text-slate-400">(optional, shown to buyers)</span>
                  </label>
                  <input
                    type="text"
                    value={ticket.note || ''}
                    onChange={(e) => { setEditingTicket(index); dispatch(updateTicketType({ index, field: 'note', value: e.target.value })); }}
                    placeholder="e.g. Includes dinner, 18+ only"
                    maxLength="120"
                    className={getInputClasses(false)}
                  />
                  <p className="text-xs text-slate-400 mt-1 text-right">{(ticket.note || '').length}/120</p>
                </div>

                {/* Bundle: one price admits several people. When on, the Price field above is the
                    whole-bundle price and must divide evenly by the bundle size. */}
                <div className="mt-3">
                  <label className="flex items-center gap-2 cursor-pointer w-fit">
                    <input
                      type="checkbox"
                      checked={isBundle(ticket)}
                      disabled={soldLock}
                      onChange={(e) => {
                        setEditingTicket(index);
                        dispatch(updateTicketType({ index, field: 'bundleSize', value: e.target.checked ? 2 : '' }));
                        clearTicketRowError(index, 'bundle');
                      }}
                      className="h-4 w-4 rounded border-slate-300 accent-teal-700 focus:ring-teal-300 disabled:cursor-not-allowed"
                    />
                    <span className="text-xs font-medium text-slate-700">Sell as a bundle (one price admits several people)</span>
                  </label>
                  {isBundle(ticket) && (
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        Bundle size (people per purchase) <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="number"
                        min="2"
                        step="1"
                        value={ticket.bundleSize === '' || ticket.bundleSize === null || ticket.bundleSize === undefined ? '' : ticket.bundleSize}
                        onChange={(e) => { setEditingTicket(index); dispatch(updateTicketType({ index, field: 'bundleSize', value: e.target.value })); clearTicketRowError(index, 'bundle'); }}
                        onWheel={(e) => e.target.blur()}
                        disabled={soldLock}
                        placeholder="4"
                        className={getInputClasses(!!ticketBundleError(index)) + soldClass}
                      />
                      <p className="text-xs text-slate-400 mt-1">The Price above is the whole pack price (e.g. $50 admits 3). Any amount is fine as long as each person's share stays at least $1.</p>
                      {ticketBundleError(index) && (
                        <p className="text-red-600 text-xs mt-1">{ticketBundleError(index)}</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-3 flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => handleCancelTicket(index)}
                    className="text-sm font-medium text-slate-500 hover:text-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDoneTicket(index)}
                    className="text-sm font-medium text-teal-700 hover:text-teal-800"
                  >
                    Done
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {errors?.ticketTypes && (
        <p className="text-red-600 text-xs mb-2">
          {Array.isArray(errors.ticketTypes) ? errors.ticketTypes[0] : errors.ticketTypes}
        </p>
      )}

      <button
        type="button"
        onClick={handleAddTicket}
        className="inline-flex items-center px-4 py-2 text-sm text-teal-700 hover:text-teal-800 font-medium"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Add Ticket Type
      </button>
    </div>
  );
};

// Discount Codes (Paid Events Only) — see feature-add-discountcode.md S1.
// Mirrors TicketTypesSection. appliesToTicketTypes stores ticket-type NAMES (explicit — no
// "all by default"). kind is 'percent' | 'fixed' to match the backend enum exactly.
const DiscountCodesSection = ({ formData, errors, dispatch }) => {
  // Accordion: which code is expanded (null = none) + per-row errors captured on Done + delete confirm.
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [showDiscountInfo, setShowDiscountInfo] = useState(false);
  const [discountRowErrors, setDiscountRowErrors] = useState({});
  const [confirmDeleteDiscount, setConfirmDeleteDiscount] = useState(null);

  const getInputClasses = (hasError) => {
    const base = 'w-full px-3 py-2.5 text-sm text-slate-700 bg-white [color-scheme:light] placeholder:text-slate-400 hover:border-slate-400 border rounded-lg focus:outline-none focus:ring-2 focus:border-teal-400 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed';
    return `${base} ${hasError ? 'border-red-500 bg-red-50 focus:ring-red-300' : 'border-slate-300 focus:ring-teal-300'}`;
  };

  const clearError = (key) => {
    if (errors?.[key]) {
      const next = { ...errors };
      delete next[key];
      dispatch(setFormErrors(next));
    }
  };

  const codes = formData.discountCodes || [];
  // A code references ticket types by NAME, so only named ticket types can be covered.
  const namedTicketTypes = (formData.ticketTypes || []).filter((t) => (t?.name || '').trim());
  const eventDay = dayOf(formData.dateTime);

  // Show the per-row (Done) error first, else the global Next/Submit error.
  const ERR_KEY = { code: 'discountCode', value: 'discountValue', applies: 'discountApplies', dates: 'discountDates', uses: 'discountUses' };
  const discountFieldError = (index, field) =>
    discountRowErrors[index]?.[field] || errors?.[`${ERR_KEY[field]}_${index}`]?.[0];
  const hasGlobalDiscountError = (index) =>
    Object.values(ERR_KEY).some((k) => errors?.[`${k}_${index}`]);

  const clearDiscountRowError = (index, field) => {
    setDiscountRowErrors((prev) => {
      if (!prev[index]?.[field]) return prev;
      const nextRow = { ...prev[index] };
      delete nextRow[field];
      const next = { ...prev };
      if (Object.keys(nextRow).length) next[index] = nextRow;
      else delete next[index];
      return next;
    });
  };

  const isEmptyDiscount = (dc) =>
    !(dc?.code || '').trim() &&
    (dc?.value === null || dc?.value === undefined || dc?.value === '') &&
    (dc?.maxUses === null || dc?.maxUses === undefined) &&
    !dc?.validFrom && !dc?.validUntil &&
    (dc?.appliesToTicketTypes || []).length === 0;

  // Per-row validation mirrors validateDiscountCodesList in StepManager, so a row can't collapse invalid.
  const validateDiscount = (dc, index) => {
    const errs = {};
    const code = (dc.code || '').trim().toUpperCase();
    if (!code) errs.code = 'Discount code is required';
    else if (code.length < 3 || code.length > 20) errs.code = 'Code must be 3-20 characters';
    else if (codes.some((o, i) => i !== index && (o.code || '').trim().toUpperCase() === code)) {
      errs.code = 'Duplicate code — each code must be unique';
    }
    const val = Number(dc.value);
    if (dc.value === null || dc.value === undefined || dc.value === '' || isNaN(val)) {
      errs.value = 'Discount value is required';
    } else if (dc.kind === 'percent') {
      if (!Number.isInteger(val) || val < 1 || val > 90) errs.value = 'Percent must be a whole number from 1 to 90';
    } else if (val <= 0) {
      errs.value = 'Amount must be greater than 0';
    } else if (Math.abs(val * 100 - Math.round(val * 100)) > 1e-9) {
      errs.value = 'Amount can have at most 2 decimal places';
    }
    const ticketNames = new Set(namedTicketTypes.map((t) => t.name));
    const applies = (dc.appliesToTicketTypes || []).filter((n) => ticketNames.has(n));
    if (dc.active !== false && applies.length === 0) {
      errs.applies = 'Pick at least one ticket type this code applies to';
    }
    const vToday = dayOf(new Date());
    const vFrom = dayOf(dc.validFrom);
    const vUntil = dayOf(dc.validUntil);
    if (vFrom && vFrom < vToday) errs.dates = "Valid from can't be in the past";
    else if (vUntil && vUntil < vToday) errs.dates = "Valid until can't be in the past";
    else if (eventDay && vFrom && vFrom > eventDay) errs.dates = "Valid from can't be after the event day";
    else if (eventDay && vUntil && vUntil > eventDay) errs.dates = "Valid until can't be after the event day";
    else if (vFrom && vUntil && vFrom > vUntil) errs.dates = '"Valid from" must be on or before "valid until"';
    const used = dc.usedCount || 0;
    if (dc.maxUses != null) {
      if (!Number.isInteger(dc.maxUses) || dc.maxUses < 1) errs.uses = 'Max uses must be a whole number (1 or more)';
      else if (dc.maxUses < used) errs.uses = `${used} already used — max uses cannot be below ${used}`;
    }
    if (applies.length > 0 && !isNaN(val)) {
      // The code may not drop any covered ticket - or any per-person share inside a covered pack -
      // below $1. For a bundle the floor is the per-person share (packPrice / size); a fixed amount
      // reduces each share by value / size. Mirrors the server (events-bundle-pricing.md D9).
      const offender = (formData.ticketTypes || [])
        .filter((t) => applies.includes(t.name) && t.price !== null && t.price !== undefined && t.price !== '' && !isNaN(Number(t.price)))
        .find((t) => {
          const size = t.bundleSize && Number(t.bundleSize) >= 2 ? Number(t.bundleSize) : 1;
          const perPerson = Number(t.price) / size;
          const after = dc.kind === 'percent' ? perPerson * (1 - val / 100) : perPerson - val / size;
          return Math.round(after * 100) < 100;
        });
      if (offender) errs.value = `This leaves ${offender.name} under the $1 minimum per ticket. Lower the discount or uncheck that ticket type.`;
    }
    return errs;
  };

  // Never collapse a blank or invalid row, so its error is always reachable before Next.
  const isDiscountOpen = (dc, index) =>
    editingDiscount === index || isEmptyDiscount(dc) || !!discountRowErrors[index] || hasGlobalDiscountError(index);

  const handleAddDiscount = () => {
    const newIndex = codes.length;
    dispatch(addDiscountCode());
    setEditingDiscount(newIndex); // open the new row
  };

  const handleRemoveDiscount = (index) => {
    dispatch(removeDiscountCode(index));
    setEditingDiscount(null);
    setDiscountRowErrors({});
  };

  const handleDoneDiscount = (index) => {
    const dc = codes[index];
    if (isEmptyDiscount(dc)) {
      dispatch(removeDiscountCode(index)); // discard a blank row
      setEditingDiscount(null);
      return;
    }
    const errs = validateDiscount(dc, index);
    if (Object.keys(errs).length > 0) {
      setDiscountRowErrors((prev) => ({ ...prev, [index]: errs }));
      setEditingDiscount(index); // keep open to show errors
      return;
    }
    setDiscountRowErrors((prev) => { const next = { ...prev }; delete next[index]; return next; });
    setEditingDiscount(null); // valid -> collapse
  };

  const handleCancelDiscount = (index) => {
    const dc = codes[index];
    if (isEmptyDiscount(dc)) {
      dispatch(removeDiscountCode(index)); // nothing entered — discard
    } else {
      // Active code left without a ticket type would be invalid — keep it but deactivate it
      // (an inactive code is valid without a ticket) rather than leave an invalid active code.
      const ticketNames = new Set(namedTicketTypes.map((t) => t.name));
      const hasTicket = (dc.appliesToTicketTypes || []).some((n) => ticketNames.has(n));
      if (dc.active !== false && !hasTicket) {
        dispatch(updateDiscountCode({ index, field: 'active', value: false }));
      }
    }
    setDiscountRowErrors((prev) => { const next = { ...prev }; delete next[index]; return next; });
    setEditingDiscount(null);
  };

  const toggleApplies = (index, name, checked) => {
    setEditingDiscount(index);
    const current = codes[index]?.appliesToTicketTypes || [];
    const updated = checked ? [...new Set([...current, name])] : current.filter((nm) => nm !== name);
    dispatch(updateDiscountCode({ index, field: 'appliesToTicketTypes', value: updated }));
    // Linkage: selecting a ticket on an inactive code auto-activates it (organiser intent);
    // unticking the last ticket auto-deactivates it (an active code can't have no ticket).
    if (checked && codes[index]?.active === false) {
      dispatch(updateDiscountCode({ index, field: 'active', value: true }));
    } else if (!checked && updated.length === 0 && codes[index]?.active !== false) {
      dispatch(updateDiscountCode({ index, field: 'active', value: false }));
    }
    clearError(`discountApplies_${index}`);
    clearDiscountRowError(index, 'applies');
  };

  const fmtDiscountValue = (dc) => (dc.kind === 'fixed' ? `$${Number(dc.value).toFixed(2)}` : `${dc.value}%`);

  return (
    <div className="border-t border-slate-200 pt-6 mt-6">
      <div className="mb-4 flex items-center gap-2">
        <h3 className="font-medium text-sm text-slate-700">
          Discount Codes
        </h3>
        <button
          type="button"
          onClick={() => setShowDiscountInfo(true)}
          aria-label="How discount codes work"
          className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          title="How discount codes work"
        >
          <FaInfoCircle className="text-lg" />
        </button>
      </div>

      {codes.length > 0 && (
        <div className="space-y-3 mb-4">
          {codes.map((dc, index) => {
            // Edit-mode locks: a saved code's word is immutable; once it's been used, kind/value/
            // valid-from also lock and it can't be deleted (deactivate instead). See feature doc.
            const dcUsed = (dc.usedCount || 0) > 0;
            const lockWord = dc.existing === true;     // saved code → word immutable
            const lockUsed = lockWord && dcUsed;       // used → kind/value/valid-from locked, no delete
            // Collapsed summary row: CODE · value (+ Inactive) + edit/delete icons.
            if (!isDiscountOpen(dc, index)) {
              return (
                <div
                  key={index}
                  className="flex items-center justify-between gap-2 p-3 border border-slate-200 rounded-lg"
                >
                  <p className="min-w-0 truncate text-sm font-medium text-slate-700">
                    {dc.code}
                    <span className="font-normal text-slate-500"> · {fmtDiscountValue(dc)}</span>
                    {(dc.appliesToTicketTypes || []).length > 0 && (
                      <span className="font-normal text-slate-500"> · {(dc.appliesToTicketTypes || []).join(', ')}</span>
                    )}
                    {dc.active === false && <span className="font-normal text-slate-400"> · Inactive</span>}
                  </p>
                  <div className="flex items-center gap-1 shrink-0">
                    {confirmDeleteDiscount === index ? (
                      <span className="flex items-center gap-2 text-xs text-slate-600">
                        Delete?
                        <button
                          type="button"
                          onClick={() => { handleRemoveDiscount(index); setConfirmDeleteDiscount(null); }}
                          className="font-medium text-red-600 hover:text-red-700"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteDiscount(null)}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          No
                        </button>
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setEditingDiscount(index)}
                          aria-label="Edit discount code"
                          className="p-2 text-slate-500 hover:text-teal-700 hover:bg-slate-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteDiscount(index)}
                          disabled={lockUsed}
                          aria-label="Delete discount code"
                          title={lockUsed ? "This code has been used — switch it off instead of deleting" : undefined}
                          className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            }

            // Expanded editor.
            return (
              <div key={index} className="border border-slate-200 rounded-lg p-3">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-700">
                    Code {index + 1}{dc?.code ? `: ${dc.code}` : ''}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className={`text-xs ${dc?.active !== false ? 'text-teal-700 font-medium' : 'text-slate-400'}`}>
                      {dc?.active !== false ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={dc?.active !== false}
                      disabled={namedTicketTypes.length === 0 && dc?.active === false}
                      title={namedTicketTypes.length === 0 ? 'Add a named ticket type first' : undefined}
                      onClick={() => { setEditingDiscount(index); dispatch(updateDiscountCode({ index, field: 'active', value: !(dc?.active !== false) })); clearDiscountRowError(index, 'applies'); }}
                      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed ${dc?.active !== false ? 'bg-teal-700' : 'bg-slate-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${dc?.active !== false ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                    </button>
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Code */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Code <span className="text-red-600">*</span></label>
                    <input
                      type="text"
                      value={dc?.code || ''}
                      onChange={(e) => { setEditingDiscount(index); dispatch(updateDiscountCode({ index, field: 'code', value: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })); clearError(`discountCode_${index}`); clearDiscountRowError(index, 'code'); }}
                      placeholder="e.g. 10OFF"
                      maxLength="20"
                      disabled={lockWord}
                      title={lockWord ? "A saved code can't be renamed — delete it (if unused) or switch it off and make a new one" : undefined}
                      className={getInputClasses(!!discountFieldError(index, 'code'))}
                    />
                    {discountFieldError(index, 'code') && (
                      <p className="text-red-600 text-xs mt-1">{discountFieldError(index, 'code')}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-1">
                      {lockWord ? "A saved code can't be renamed." : 'Max 20 characters, letters and numbers only.'}
                    </p>
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Type <span className="text-red-600">*</span></label>
                    <select
                      value={dc?.kind || 'percent'}
                      onChange={(e) => { setEditingDiscount(index); dispatch(updateDiscountCode({ index, field: 'kind', value: e.target.value })); clearDiscountRowError(index, 'value'); }}
                      disabled={lockUsed}
                      title={lockUsed ? "Locked — this code has been used" : undefined}
                      className={getInputClasses(false)}
                    >
                      <option value="percent">Percentage (%)</option>
                      <option value="fixed">Fixed amount ($)</option>
                    </select>
                  </div>

                  {/* Value */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      {dc?.kind === 'fixed' ? 'Amount ($)' : 'Percent (1–90)'} <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      min={dc?.kind === 'fixed' ? '0.01' : '1'}
                      max={dc?.kind === 'fixed' ? undefined : '90'}
                      step={dc?.kind === 'fixed' ? '0.01' : '1'}
                      value={dc.value === null || dc.value === undefined ? '' : dc.value}
                      onChange={(e) => { setEditingDiscount(index); const v = e.target.value; dispatch(updateDiscountCode({ index, field: 'value', value: v === '' ? null : Number(v) })); clearError(`discountValue_${index}`); clearDiscountRowError(index, 'value'); }}
                      onWheel={(e) => e.target.blur()}
                      disabled={lockUsed}
                      title={lockUsed ? "Locked — this code has been used" : undefined}
                      className={getInputClasses(!!discountFieldError(index, 'value'))}
                      placeholder={dc?.kind === 'fixed' ? '5.00' : '10'}
                    />
                    {discountFieldError(index, 'value') && <p className="text-red-600 text-xs mt-1">{discountFieldError(index, 'value')}</p>}
                  </div>

                  {/* Max uses */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Max uses <span className="font-normal text-slate-400">(optional)</span></label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={dc.maxUses === null || dc.maxUses === undefined ? '' : dc.maxUses}
                      onChange={(e) => { setEditingDiscount(index); const v = e.target.value; dispatch(updateDiscountCode({ index, field: 'maxUses', value: v === '' ? null : Number(v) })); clearError(`discountUses_${index}`); clearDiscountRowError(index, 'uses'); }}
                      onWheel={(e) => e.target.blur()}
                      className={getInputClasses(!!discountFieldError(index, 'uses'))}
                      placeholder="Unlimited"
                    />
                    {discountFieldError(index, 'uses') && <p className="text-red-600 text-xs mt-1">{discountFieldError(index, 'uses')}</p>}
                  </div>

                  {/* Valid from */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Valid from <span className="font-normal text-slate-400">(optional)</span></label>
                    <input
                      type="date"
                      value={dc.validFrom ? (dc.validFrom.includes('T') ? dc.validFrom.slice(0, 10) : dc.validFrom) : ''}
                      onChange={(e) => { setEditingDiscount(index); dispatch(updateDiscountCode({ index, field: 'validFrom', value: e.target.value || null })); clearError(`discountDates_${index}`); clearDiscountRowError(index, 'dates'); }}
                      max={eventDay || undefined}
                      disabled={lockUsed}
                      title={lockUsed ? "Locked — this code has been used" : undefined}
                      className={getInputClasses(!!discountFieldError(index, 'dates'))}
                    />
                  </div>

                  {/* Valid until */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Valid until <span className="font-normal text-slate-400">(optional)</span></label>
                    <input
                      type="date"
                      value={dc.validUntil ? (dc.validUntil.includes('T') ? dc.validUntil.slice(0, 10) : dc.validUntil) : ''}
                      onChange={(e) => { setEditingDiscount(index); dispatch(updateDiscountCode({ index, field: 'validUntil', value: e.target.value || null })); clearError(`discountDates_${index}`); clearDiscountRowError(index, 'dates'); }}
                      min={dayOf(dc.validFrom) || undefined}
                      max={eventDay || undefined}
                      className={getInputClasses(!!discountFieldError(index, 'dates'))}
                    />
                  </div>
                </div>

                {discountFieldError(index, 'dates') && <p className="text-red-600 text-xs mt-1">{discountFieldError(index, 'dates')}</p>}

                {/* Applies to (ticket-type NAMES, explicit) */}
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <label className="block text-xs font-medium text-slate-700 mb-2">
                    Select ticket types to discount <span className="text-red-600">*</span>
                  </label>
                  {namedTicketTypes.length === 0 ? (
                    <p className="text-xs text-amber-600">Name your ticket types above first, then choose which ones this code applies to.</p>
                  ) : (
                    <div className="flex flex-wrap gap-3">
                      {namedTicketTypes.map((t) => (
                        <label key={t.name} className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            checked={(dc.appliesToTicketTypes || []).includes(t.name)}
                            onChange={(e) => toggleApplies(index, t.name, e.target.checked)}
                            className="w-4 h-4 accent-teal-700 border-slate-300 rounded focus:ring-teal-300"
                          />
                          <span className="text-sm text-slate-700">{t.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {discountFieldError(index, 'applies') && <p className="text-red-600 text-xs mt-1">{discountFieldError(index, 'applies')}</p>}
                </div>

                {/* Advertise at ticket selection — yes/no toggle (only meaningful when the code is active) */}
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={dc?.showHint === true && dc?.active !== false}
                    disabled={dc?.active === false}
                    title={dc?.active === false ? 'Activate the code to advertise it' : undefined}
                    onClick={() => { setEditingDiscount(index); dispatch(updateDiscountCode({ index, field: 'showHint', value: !(dc?.showHint === true) })); }}
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed ${(dc?.showHint === true && dc?.active !== false) ? 'bg-teal-700' : 'bg-slate-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${(dc?.showHint === true && dc?.active !== false) ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                  </button>
                  <span className={`text-sm ${dc?.active === false ? 'text-slate-400' : 'text-slate-700'}`}>
                    {dc?.code ? `Advertise "${dc.code}" at ticket selection` : 'Advertise this code at ticket selection'}
                    {dc?.active === false ? ' (activate first)' : ''}
                  </span>
                </div>

                <div className="mt-3 flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => handleCancelDiscount(index)}
                    className="text-sm font-medium text-slate-500 hover:text-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDoneDiscount(index)}
                    className="text-sm font-medium text-teal-700 hover:text-teal-800"
                  >
                    Done
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={handleAddDiscount}
        className="inline-flex items-center px-4 py-2 text-sm text-teal-700 hover:text-teal-800 font-medium"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Add Discount Code
      </button>

      {/* How discount codes work — info popup */}
      {showDiscountInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50" onClick={() => setShowDiscountInfo(false)}>
          <div className="bg-white w-full sm:max-w-md rounded-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-900">How discount codes work</h3>
              <button
                type="button"
                onClick={() => setShowDiscountInfo(false)}
                aria-label="Close"
                className="p-1 text-slate-400 hover:text-slate-600 rounded"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <ul className="space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>Buyers type the code at checkout. Codes are 3–20 letters/numbers, shown in capitals.</li>
                <li>A code only works when <strong>Active</strong> — and an active code must apply to at least one ticket type (ticking a ticket activates it automatically).</li>
                <li>The discount is a percentage (1–90%) or a fixed $ amount, and can't bring a ticket under $1.</li>
                <li><strong>Advertise at ticket selection</strong>: on = the code is shown publicly on the tickets page; off = secret (buyers must know it).</li>
                <li>Once a code has been used, you can't change its name, type, value or start date, and can't delete it — switch it <strong>off</strong> to stop it.</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// GST Configuration
const GSTConfiguration = ({ formData, dispatch }) => {
  const on = formData.gstIncluded || false;
  // Preview uses the cheapest entered ticket price, else a $100 example.
  const prices = (formData.ticketTypes || [])
    .map((t) => Number(t.price))
    .filter((p) => !isNaN(p) && p > 0);
  const sampleLabel = `$${(prices.length ? Math.min(...prices) : 100).toFixed(2)}`;

  return (
    <div className="border-t border-slate-200 pt-6 mt-6">
      <h3 className="font-medium text-sm mb-3 text-slate-700">GST</h3>

      <div className="flex items-start gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={on}
          onClick={() => dispatch(updateGstIncluded(!on))}
          className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-300 ${on ? 'bg-teal-700' : 'bg-slate-200'}`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
        </button>
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-700">Ticket prices include GST</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Turn on if your ticket prices already include 10% GST.
          </p>

          {/* Live preview of the checkout total line */}
          <div className="mt-3 max-w-xs">
            <p className="text-xs text-slate-500 mb-1">At checkout, the total reads:</p>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <span className="text-slate-700">{on ? 'Total (incl. GST)' : 'Total'}</span>
              <span className="font-medium text-slate-900">{sampleLabel}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main RegistrationStep Component
const RegistrationStep = ({
  formData = {},
  errors = {},
  mode = 'add',
  readOnly = false,
  onSeatingImagesChange
}) => {
  const dispatch = useDispatch();
  const isEditMode = mode === 'edit';
  const isViewMode = mode === 'view';
  const disabled = readOnly || isViewMode;

  const isRecurringEvent = formData.recurrence?.isRecurring || false;
  const isPromotionalEvent = formData.eventIntent === 'promote';
  const isHostEvent = formData.eventIntent === 'host';
  const isPaidEvent = formData.isPaid || false;

  return (
    <div className="space-y-6">
      {/* Promotional Event Configuration */}
      {isPromotionalEvent && (
        <PromotionalConfig
          formData={formData}
          disabled={disabled}
          dispatch={dispatch}
          errors={errors}
        />
      )}

      {/* Host Event Configuration */}
      {isHostEvent && (
        <>
          {/* Configuration based on Free/Paid */}
          <div className="space-y-6">
            {/* Capacity Management */}
            <CapacityManagement
              formData={formData}
              errors={errors}
              dispatch={dispatch}
              isPaid={isPaidEvent}
            />

            {/* Ticket Types (Paid Only) */}
            {isPaidEvent && (
              <TicketTypesSection
                formData={formData}
                errors={errors}
                dispatch={dispatch}
              />
            )}

            {/* GST Configuration (Paid Only) - sits with ticket pricing, before discounts */}
            {isPaidEvent && (
              <GSTConfiguration
                formData={formData}
                dispatch={dispatch}
              />
            )}

            {/* Discount Codes (Paid Only) */}
            {isPaidEvent && (
              <DiscountCodesSection
                formData={formData}
                errors={errors}
                dispatch={dispatch}
              />
            )}

            {/* Seating Layout (Paid Only) */}
            {isPaidEvent && (
              <SeatingLayoutSection
                formData={formData}
                errors={errors}
                dispatch={dispatch}
                isEditMode={isEditMode}
                onSeatingImagesChange={onSeatingImagesChange}
              />
            )}

            {/* Attendee Information */}
            <CustomRegistrationFields
              formData={formData}
              errors={errors}
              dispatch={dispatch}
              isRecurring={isRecurringEvent}
            />
          </div>
        </>
      )}

      {/* No Intent Selected - Should not happen since intent is in Step 1 */}
      {!formData.eventIntent && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
          <svg className="w-10 h-10 mx-auto text-amber-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-amber-700 font-medium">Event type not selected</p>
          <p className="text-amber-600 text-sm mt-1">Please go back to Step 1 and select whether you want to promote or host this event.</p>
        </div>
      )}
    </div>
  );
};

export default RegistrationStep;
